// API service for Raisa.chat
// Security: No sensitive data in localStorage, use httpOnly cookies for auth

const API_BASE = '/api';

// =============================================================================
// ROUTE TYPES - Transport timeline
// =============================================================================

export type TransportType = 'flight' | 'train' | 'car' | 'bus' | 'ferry' | 'unknown';

/**
 * City reference with GeoNames ID as source of truth.
 * Label is cached for display, cityId is the canonical identifier.
 */
export interface CityRef {
    cityId?: number;              // GeoNames ID (source of truth)
    label: string;                // Display name (cache)
    geoQuery?: string;            // Official name for geocoding
    countryCode?: string;         // ISO 3166-1 alpha-2
    lat?: number;
    lng?: number;
    source?: 'user' | 'ip' | 'ai' | 'unknown';
}

/**
 * Transport leg between two points.
 * Durations/distances as numbers - format in UI per locale.
 */
export interface TransportLeg {
    type: TransportType;
    fromCityId?: number;          // GeoNames ID of origin
    fromLabel?: string;           // Display cache
    durationMin?: number;         // Duration in minutes
    distanceKm?: number;          // Distance in km
    priceCents?: number;          // Price in cents
    currency?: string;            // ISO 4217 (EUR, USD, etc.)
}

/**
 * Route leg connecting to a city in the trip.
 */
export interface RouteLeg {
    toCityIdx: number;            // Index in cities[]
    transport: TransportLeg;
}

/**
 * Return leg configuration.
 */
export interface ReturnLeg {
    enabled: boolean;
    transport?: TransportLeg;
    toOrigin: boolean;            // false = open jaw
}

/**
 * Complete route structure for a trip.
 */
export interface Route {
    origin?: CityRef;
    legs: RouteLeg[];
    return?: ReturnLeg;
}

// =============================================================================
// TRIP CONTENT TYPES
// =============================================================================

/**
 * Geo data - resolved coordinates from geocoder
 * 
 * RULES:
 * - status: pending     → source: absent,  lat/lng: null
 * - status: approximate → source: geonames | fallback,  lat/lng: city center
 * - status: resolved    → source: nominatim | mapbox,   lat/lng: POI coords
 */
export type GeoStatus = 'pending' | 'resolved' | 'approximate';
export type GeoConfidence = 'high' | 'medium' | 'low';
export type GeoSource = 'nominatim' | 'mapbox' | 'geonames' | 'fallback';

export interface Geo {
    lat: number | null;  // null for pending status
    lng: number | null;  // null for pending status
    status: GeoStatus;
    confidence?: GeoConfidence;
    source?: GeoSource;  // absent for pending status
    placeId?: string;
    resolvedAt?: string;
}

export interface TransferDetails {
    from: string;
    fromGeoQuery?: string | null;  // Official name for geocoding origin
    to: string;
    toGeoQuery?: string | null;    // Official name for geocoding destination
    mode: 'metro' | 'bus' | 'taxi' | 'uber' | 'tram' | 'boat' | 'cable_car' | 'walking' | 'other';
    durationMin?: number;
    cost?: string;
    line?: string;
    notes?: string;
}

export interface Activity {
    id: string;
    time: string;
    title: string;
    description: string;
    location: { 
        name: string;        // Human-friendly label for UI
        geoQuery?: string | null;  // Official name for geocoder (null = generic)
    };
    geo?: Geo;  // Resolved: coordinates from geocoder
    requiresTicket?: boolean;
    confidenceScore?: 'high' | 'medium' | 'estimate';  // @deprecated - use geo.confidence
    blockType?: string;
    arrivalLocationType?: 'AIRPORT' | 'TRAIN' | 'CAR' | 'BUS' | 'FERRY' | 'UNKNOWN';  // For SYSTEM_ARRIVAL/DEPARTURE
    transferDetails?: TransferDetails;  // For SYSTEM_TRANSFER blocks
    travelToNext?: {
        walking?: { duration: number; distance: number };
        driving?: { duration: number; distance: number };
        transit?: { duration: number; isEstimate?: boolean };
    };
}

export interface TripContent {
    title: string;
    dateRange: { start: string; end: string };
    /** Optional aggregate country label (used by some UI surfaces). */
    destinationCountry?: string;
    /** Optional aggregate day count (used by some UI surfaces). */
    totalDays?: number;
    defaultAccommodation?: {
        label: string;
        location?: { 
            name: string;
            geoQuery?: string | null;  // Official name for geocoding
        };
        geo?: Geo;
    };
    cities: Array<{
        id: string;
        name: string;
        country: string;
        centerGeoQuery?: string | null;  // Main square/center for geocoding fallback
        transportGeoQueries?: {
            airport?: string | null;       // "Aeroporto di Roma-Fiumicino"
            trainStation?: string | null;  // "Roma Termini"
            busStation?: string | null;    // "Autostazione Roma Tiburtina"
            port?: string | null;          // "Porto di Civitavecchia"
        };
        dateRange: { start: string; end: string };
        stay?: { 
            name: string;
            geoQuery?: string | null;  // Official name for accommodation geocoding
        };
        days: Array<{
            date: string;
            /** Short catchy title for the day (e.g. "Arrival & Downtown Exploration") */
            theme?: string;
            /** 1–2 emotional sentences describing what this day is about */
            description?: string;
            activities: Activity[];
        }>;
    }>;
    route?: Route;  // Route timeline with origin, legs, return
}

/**
 * Format duration in minutes to human-readable string.
 * Example: 150 -> "2h 30m"
 */
export function formatDuration(minutes: number | undefined): string {
    if (!minutes) return '';
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours === 0) return `${mins}m`;
    if (mins === 0) return `${hours}h`;
    return `${hours}h ${mins}m`;
}

export interface Trip {
    id: string;
    slug: string;
    isPublic: boolean;
    isTemplate: boolean;
    language: string;
    currency: string;
    createdAt: string;
    generationStatus?: 'generating' | 'partial' | 'ready' | 'failed' | null;
    generationError?: string | null;
}

// Itinerary access control types
export type RestrictionReason = 
    | 'NOT_LOGGED_IN' 
    | 'NOT_PREMIUM' 
    | 'NO_TRIP_ACCESS' 
    | 'TOKEN_VIEW_ONLY' 
    | 'FULL_ACCESS';

export interface ItineraryAccess {
    canSeeAllDays: boolean;
    canAddDay: boolean;
    isRestricted: boolean;
    reason: RestrictionReason;
    totalDays: number;
    daysPerCity: number[];
    daysVisible: number;
}

export interface GenerateTripResponse {
    trip: Trip;
    content: TripContent | null;
    versionNo: number;
    itineraryAccess?: ItineraryAccess;
    seo?: {
        noindex: boolean;
    };
}

// Get trip generation status
export async function getTripStatus(slug: string): Promise<{
    tripId: string;
    slug: string;
    status: string;
    ready: boolean;
    error?: string;
}> {
    const response = await fetch(`${API_BASE}/trip/${slug}/status`, {
        credentials: 'include',
    });
    return response.json();
}

export interface ApiError {
    error: string;
    requiresCaptcha?: boolean;
    details?: Record<string, string[]>;
}

// Generate trip
export async function generateTrip(
    prompt: string,
    options?: { language?: string; currency?: string; captchaToken?: string }
): Promise<GenerateTripResponse> {
    const response = await fetch(`${API_BASE}/trip/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include', // Include cookies for auth
        body: JSON.stringify({
            prompt,
            language: options?.language || 'en',
            currency: options?.currency || 'EUR',
            captchaToken: options?.captchaToken,
        }),
    });

    const data = await response.json();

    if (!response.ok) {
        throw data as ApiError;
    }

    return data as GenerateTripResponse;
}

// Get trip by slug
// If editToken is provided, it's sent in X-Edit-Token header for shared link access (view-only)
export async function getTripBySlug(slug: string, editToken?: string): Promise<GenerateTripResponse | null> {
    const headers: Record<string, string> = {};
    if (editToken) {
        headers['X-Edit-Token'] = editToken;
    }

    const response = await fetch(`${API_BASE}/trip/${slug}`, {
        credentials: 'include',
        headers,
    });

    if (response.status === 404) {
        return null;
    }

    if (!response.ok) {
        const data = await response.json();
        throw data as ApiError;
    }

    return response.json();
}

export interface TripInfo {
    trip: {
        id: string;
        slug: string;
        ownerId: string | null;
        isPublic: boolean;
        isTemplate: boolean;
        language: string;
        currency: string;
        createdAt: string;
    };
    content: TripContent;
    versionNo: number;
    hasTripAccess?: boolean; // true if user purchased this trip
}

// Get user trips
export async function getUserTrips(): Promise<{ trips: TripInfo[] }> {
    const response = await fetch(`${API_BASE}/trip`, {
        credentials: 'include',
    });

    if (!response.ok) {
        throw await response.json() as ApiError;
    }

    return response.json();
}

// Claim anonymous trip
export async function claimTrip(slug: string): Promise<{ success: boolean }> {
    const response = await fetch(`${API_BASE}/trip/${slug}/claim`, {
        method: 'POST',
        credentials: 'include',
    });

    const data = await response.json();

    if (!response.ok) {
        throw data as ApiError;
    }

    return data;
}

// Update trip visibility
export async function updateTripVisibility(slug: string, isPublic: boolean): Promise<{ success: boolean }> {
    const response = await fetch(`${API_BASE}/trip/${slug}/visibility`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ isPublic }),
    });

    const data = await response.json();

    if (!response.ok) {
        throw data as ApiError;
    }

    return data;
}

// Customize trip (create working copy from template)
export async function customizeTrip(slug: string): Promise<{ success: boolean; slug: string }> {
    const response = await fetch(`${API_BASE}/trip/${slug}/customize`, {
        method: 'POST',
        credentials: 'include',
    });

    const data = await response.json();

    if (!response.ok) {
        throw data as ApiError;
    }

    return data;
}

// Clone trip
export async function cloneTrip(slug: string): Promise<{ success: boolean; slug: string }> {
    const response = await fetch(`${API_BASE}/trip/${slug}/clone`, {
        method: 'POST',
        credentials: 'include',
    });

    const data = await response.json();

    if (!response.ok) {
        throw data as ApiError;
    }

    return data;
}

// User status types
export interface UserStatus {
    isAuthenticated: boolean;
    isOwner: boolean;
    canModifyTrip: boolean;
    hasEditsAvailable: boolean;
    isPublic: boolean;
    freeEditsUsed: number;
    freeEditsRemaining: number;
    isPremium: boolean;
    isTripUnlocked: boolean; // One-time unlock for this specific trip
    dailyEditsToday: number;
    dailyLimitReached: boolean;
    isEligibleForLaunchPromo?: boolean; // First-time buyer gets €2.99 instead of €9.99
    subscription?: {
        status: string;
        currentPeriodEnd: string | null;
        cancelAtPeriodEnd: boolean;
    } | null;
    settings?: {
        privacy?: {
            marketing?: boolean;
            analytics?: boolean;
            affiliates?: boolean;
        };
    };
}

// Get user status (edit entitlements)
export async function getUserStatus(tripSlug?: string): Promise<UserStatus> {
    const url = tripSlug ? `${API_BASE}/user/status?tripSlug=${tripSlug}` : `${API_BASE}/user/status`;
    const response = await fetch(url, {
        credentials: 'include',
    });

    // Parse response body even for 401 (contains isEligibleForLaunchPromo for non-auth users)
    const data = await response.json().catch(() => ({}));

    if (!response.ok) {
        // Return status from backend if available, otherwise defaults
        return {
            isAuthenticated: false,
            isOwner: false,
            canModifyTrip: false,
            hasEditsAvailable: false,
            isPublic: false,
            freeEditsUsed: 0,
            freeEditsRemaining: data.freeEditsRemaining ?? 0,
            isPremium: false,
            isTripUnlocked: false,
            dailyEditsToday: 0,
            dailyLimitReached: false,
            isEligibleForLaunchPromo: data.isEligibleForLaunchPromo ?? false,
        };
    }

    return data;
}

// =============================================================================
// USER LOCATION (IP Geolocation)
// =============================================================================

export interface UserLocation {
    city: string | null;
    region: string | null;
    country: string | null;
    countryCode: string | null;
    lat: number | null;
    lon: number | null;
    timezone: string | null;
    source: 'ip' | 'manual' | 'gps';
    confidence: 'approximate' | 'precise';
}

/**
 * Get user's approximate location from IP address
 * Used for suggesting trip origin
 */
export async function getUserLocation(): Promise<UserLocation | null> {
    try {
        const response = await fetch(`${API_BASE}/user/location`, {
            credentials: 'include',
        });

        if (!response.ok) {
            return null;
        }

        const data = await response.json();
        return data.location || null;
    } catch {
        return null;
    }
}

// Checkout types
export interface CheckoutResponse {
    sessionUrl: string;
}

// Create trip unlock checkout
export async function createTripUnlockCheckout(tripSlug: string): Promise<CheckoutResponse> {
    const response = await fetch(`${API_BASE}/checkout/trip-unlock`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ tripSlug }),
    });

    if (!response.ok) {
        const data = await response.json();
        throw data as ApiError;
    }

    return response.json();
}

// Create subscription checkout
export async function createSubscriptionCheckout(plan: string = 'annual'): Promise<CheckoutResponse> {
    const response = await fetch(`${API_BASE}/checkout/subscription`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ plan }),
    });

    if (!response.ok) {
        const data = await response.json();
        throw data as ApiError;
    }

    return response.json();
}

// Create Stripe customer portal session
export async function createPortalSession(): Promise<CheckoutResponse> {
    const response = await fetch(`${API_BASE}/checkout/portal`, {
        method: 'POST',
        credentials: 'include',
    });

    if (!response.ok) {
        const data = await response.json();
        throw data as ApiError;
    }

    return response.json();
}

// AI Edit types
export interface PatchResultSummary {
    locale: 'en' | 'ro';
    title: string;
    bullets: string[];
    nextSuggestion?: string;
    uiBadges?: { type: 'success' | 'warn' | 'info'; label: string }[];
}

export interface AiEditResponse {
    success: boolean;
    content?: TripContent;
    versionNo?: number;
    message?: string;
    error?: string;
    freeEditsRemaining?: number;
    patchApplied?: string;
    summary?: PatchResultSummary;
}

// Apply AI edit to trip
export interface AiEditContext {
    /** User is on a newly added empty day; AI may ask about day n-1 (e.g. same/different accommodation). */
    emptyDay?: boolean;
    cityIndex?: number;
    dayIndex?: number;
}

/**
 * @param locale - Platform language (e.g. "en", "ro"). Raisa will reply only in this language; user may write in any language.
 */
export async function applyAiEdit(
    tripSlug: string,
    prompt: string,
    context?: AiEditContext,
    locale?: string
): Promise<AiEditResponse> {
    const idempotencyKey = crypto.randomUUID();

    const response = await fetch(`${API_BASE}/ai/edit`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Idempotency-Key': idempotencyKey,
        },
        credentials: 'include',
        body: JSON.stringify({ tripSlug, message: prompt, context, locale }),
    });

    const data = await response.json();

    if (!response.ok) {
        throw data as ApiError;
    }

    return data;
}

// Submit patch answer during trip generation
export interface SubmitPatchResponse {
    success: boolean;
    applied: boolean;
    message?: string;
}

export async function submitPatchAnswer(
    tripId: string,
    data: { questionType: string; answer: string; editToken?: string }
): Promise<SubmitPatchResponse> {
    const response = await fetch(`${API_BASE}/trip/${tripId}/patch`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(data),
    });

    if (!response.ok) {
        const error = await response.json().catch(() => ({ error: 'Failed to submit patch' }));
        throw new Error(error.error || 'Failed to submit patch');
    }

    return response.json();
}

// Add Activity types
export interface AddActivityRequest {
    description: string;
    dayIndex: number;
    cityIndex?: number;
    time?: string;
    period?: 'morning' | 'afternoon' | 'evening';
}

export interface AddActivityResponse {
    success: boolean;
    activity: {
        id: string;
        time: string;
        title: string;
        description: string;
        location: { name: string; lat: number; lng: number };
    };
    versionNo: number;
    chatMessage: string;
}

export async function addActivity(
    tripId: string,
    data: AddActivityRequest
): Promise<AddActivityResponse> {
    const response = await fetch(`${API_BASE}/trip/${tripId}/add-activity`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(data),
    });

    if (!response.ok) {
        const error = await response.json().catch(() => ({ error: 'Failed to add activity' }));
        throw new Error(error.error || 'Failed to add activity');
    }

    return response.json();
}

export interface AddDayResponse {
    success: boolean;
    newDayIndex: number;
}

// Error with code for handling NEED_UNLOCK
export class AddDayError extends Error {
    code?: string;
    reason?: RestrictionReason;
    
    constructor(message: string, code?: string, reason?: RestrictionReason) {
        super(message);
        this.name = 'AddDayError';
        this.code = code;
        this.reason = reason;
    }
}

/**
 * Add an empty day to a city. Returns the new day index.
 * 
 * Note: editToken grants view-only access, NOT add-day permission.
 * If user doesn't have canAddDay (premium or TripAccess), throws AddDayError with code 'NEED_UNLOCK'.
 * 
 * @param tripId - Trip ID
 * @param cityIndex - City index to add day to
 * @param editToken - Optional token (sent in header, but does NOT grant add-day permission)
 */
export async function addDay(
    tripId: string,
    cityIndex: number,
    editToken?: string
): Promise<AddDayResponse> {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (editToken) {
        headers['X-Edit-Token'] = editToken;
    }

    const response = await fetch(`${API_BASE}/trip/${tripId}/add-day`, {
        method: 'POST',
        headers,
        credentials: 'include',
        body: JSON.stringify({ cityIndex }),
    });
    
    if (!response.ok) {
        const error = await response.json().catch(() => ({ error: 'Failed to add day' }));
        throw new AddDayError(
            error.error || 'Failed to add day',
            error.code,
            error.reason
        );
    }
    return response.json();
}

// =============================================================================
// ROUTE UPDATE API
// =============================================================================

export interface UpdateRouteResponse {
    success: boolean;
    versionNo: number;
    message?: string;
}

/**
 * Update trip route (transport types, origin, return leg).
 * Creates a new TripVersion (never updates in place).
 * Auto-creates ChatMessage for audit trail.
 */
export async function updateTripRoute(
    tripId: string,
    route: Route,
    editToken?: string
): Promise<UpdateRouteResponse> {
    const response = await fetch(`${API_BASE}/trip/${tripId}/route`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ route, editToken }),
    });

    if (!response.ok) {
        const error = await response.json().catch(() => ({ error: 'Failed to update route' }));
        const message = error.error || 'Failed to update route';
        const details = error.details as Record<string, string[] | undefined> | undefined;
        const detailStr = details && typeof details === 'object'
            ? Object.entries(details)
                .map(([k, v]) => (Array.isArray(v) ? `${k}: ${v.join(', ')}` : `${k}: ${v}`))
                .join('; ')
            : '';
        throw new Error(detailStr ? `${message}. ${detailStr}` : message);
    }

    return response.json();
}

// Chat message types
export interface ChatMessageFromApi {
    id: string;
    role: 'user' | 'assistant' | 'system';
    content: string;
    patchType: string | null;
    createdAt: string;
}

// Get chat messages for trip
export async function getChatMessages(tripSlug: string): Promise<ChatMessageFromApi[]> {
    const response = await fetch(`${API_BASE}/chat/${tripSlug}`, {
        method: 'GET',
        credentials: 'include',
    });

    if (!response.ok) {
        if (response.status === 403 || response.status === 401) {
            return []; // No access or not authenticated - return empty
        }
        return [];
    }

    const data = await response.json();
    return data.messages || [];
}
// Preview AI edit to trip (text response only, no patches)
export async function previewChat(
    tripSlug: string,
    message: string,
    language: string = 'en'
): Promise<{ reply: string }> {
    const response = await fetch(`${API_BASE}/ai/preview`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ tripSlug, message, language }),
    });

    const data = await response.json();

    if (!response.ok) {
        throw data as ApiError;
    }

    return data;
}

// ============================================================================
// VACATION API (My Travel Calendar)
// ============================================================================

export interface Vacation {
    id: string;
    title: string;
    startDate: string; // YYYY-MM-DD
    endDate: string;   // YYYY-MM-DD
    type: 'vacation' | 'city_break' | 'road_trip' | 'workation';
    status: 'planned' | 'confirmed' | 'finished';
    itineraryId: string | null;
    itinerary?: { id: string; slug: string } | null;
}

export interface VacationInput {
    title: string;
    startDate: string;
    endDate: string;
    type?: Vacation['type'];
    status?: Vacation['status'];
    itineraryId?: string | null;
}

export async function getVacations(year: number): Promise<{ vacations: Vacation[] }> {
    const response = await fetch(`${API_BASE}/vacations?year=${year}`, {
        credentials: 'include',
    });

    if (!response.ok) {
        const error = await response.json();
        throw error as ApiError;
    }

    return response.json();
}

export async function getVacationByItinerary(tripId: string): Promise<{ vacation: Vacation | null }> {
    const response = await fetch(`${API_BASE}/vacations/by-itinerary/${tripId}`, {
        credentials: 'include',
    });

    if (response.status === 404) {
        return { vacation: null };
    }

    if (!response.ok) {
        const error = await response.json();
        throw error as ApiError;
    }

    return response.json();
}

export async function createVacation(input: VacationInput): Promise<{ vacation: Vacation }> {
    const response = await fetch(`${API_BASE}/vacations`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(input),
    });

    if (!response.ok) {
        const error = await response.json();
        throw error as ApiError;
    }

    return response.json();
}

export async function updateVacation(id: string, input: Partial<VacationInput>): Promise<{ vacation: Vacation }> {
    const response = await fetch(`${API_BASE}/vacations/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(input),
    });

    if (!response.ok) {
        const error = await response.json();
        throw error as ApiError;
    }

    return response.json();
}

export async function deleteVacation(id: string): Promise<void> {
    const response = await fetch(`${API_BASE}/vacations/${id}`, {
        method: 'DELETE',
        credentials: 'include',
    });

    if (!response.ok) {
        const error = await response.json();
        throw error as ApiError;
    }
}

// =============================================================================
// DISCOVERY (Guided Trip Planning)
// =============================================================================

export interface DiscoveryState {
    phase: 1 | 2 | 3 | 4 | 5 | 'ready';
    data: Record<string, unknown>;
    messages: Array<{ role: 'user' | 'assistant'; content: string }>;
    summary: string;
    canGenerate: boolean;
    suggestions?: string[];
}

export interface DiscoveryChatResponse {
    reply: string;
    phase: 1 | 2 | 3 | 4 | 5 | 'ready';
    canGenerate: boolean;
    summary: string;
    suggestions?: string[];
    // FCG: Fast Commit Generation
    action?: 'GENERATE_TRIP' | 'PATCH_WHILE_GENERATING' | 'GENERATING';
    slug?: string; // returned with GENERATING action
    preferences?: Record<string, unknown>;
    // PATCH_WHILE_GENERATING fields
    tripId?: string;
    patch?: {
        question: string;
        key: string;
        options: string[];
    };
}

/**
 * Get or create anonymous session ID for discovery
 */
function getDiscoverySessionId(): string {
    const key = 'raisa_discovery_session';
    let sessionId = localStorage.getItem(key);
    if (!sessionId) {
        sessionId = 'anon_' + Math.random().toString(36).substring(2, 15) + Date.now().toString(36);
        localStorage.setItem(key, sessionId);
    }
    return sessionId;
}

/**
 * Send a message in the discovery conversation
 */
export async function sendDiscoveryMessage(message: string): Promise<DiscoveryChatResponse> {
    const response = await fetch(`${API_BASE}/chat/discovery`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'X-Session-Id': getDiscoverySessionId(),
        },
        credentials: 'include',
        body: JSON.stringify({ message }),
    });

    if (!response.ok) {
        const error = await response.json();
        throw error as ApiError;
    }

    return response.json();
}

/**
 * Get current discovery session state (for resuming)
 */
export async function getDiscoveryState(): Promise<DiscoveryState> {
    const response = await fetch(`${API_BASE}/chat/discovery/state`, {
        headers: { 'X-Session-Id': getDiscoverySessionId() },
        credentials: 'include',
    });

    if (!response.ok) {
        const error = await response.json();
        throw error as ApiError;
    }

    return response.json();
}

/**
 * Reset and start a new discovery conversation
 */
export async function resetDiscovery(): Promise<{ success: boolean; phase: number }> {
    const response = await fetch(`${API_BASE}/chat/discovery/reset`, {
        method: 'POST',
        headers: { 'X-Session-Id': getDiscoverySessionId() },
        credentials: 'include',
    });

    if (!response.ok) {
        const error = await response.json();
        throw error as ApiError;
    }

    return response.json();
}

